import { useState } from 'react'
import { Input, Button } from '../ui'
import { useTranslation } from '../../contexts/LanguageContext'

interface ForgotPasswordFormProps {
  onSubmit: (email: string) => Promise<void>
  onBackToLogin: () => void
  error?: string | null
  loading?: boolean
}

export function ForgotPasswordForm({
  onSubmit,
  onBackToLogin,
  error,
  loading,
}: ForgotPasswordFormProps) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError(null)

    if (!email.trim()) {
      setLocalError(t.auth.emailRequired)
      return
    }

    try {
      await onSubmit(email)
      setSuccess(true)
    } catch {
      // Error handled by parent
    }
  }

  const displayError = error || localError

  if (success) {
    return (
      <div className="space-y-6 text-center">
        <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
          <svg
            className="w-8 h-8 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold text-gray-900">
            {t.auth.emailSentTitle}
          </h2>
          <p className="mt-2 text-gray-600">
            {t.auth.emailSentDescription}
          </p>
        </div>
        <Button variant="secondary" onClick={onBackToLogin} className="w-full">
          {t.auth.backToLogin}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="font-display text-2xl font-bold text-gray-900">
          {t.auth.forgotPasswordTitle}
        </h2>
        <p className="mt-2 text-gray-600">
          {t.auth.forgotPasswordSubtitle}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {displayError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{displayError}</p>
          </div>
        )}

        <Input
          label={t.auth.email}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t.auth.emailPlaceholder}
          autoComplete="email"
        />

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          isLoading={loading}
        >
          {t.auth.sendEmail}
        </Button>
      </form>

      <button
        type="button"
        onClick={onBackToLogin}
        className="w-full text-sm text-gray-600 hover:text-gray-900 transition-colors flex items-center justify-center gap-2"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
        {t.auth.backToLogin}
      </button>
    </div>
  )
}
